import { FormTextArea } from '@/components/forms/form-text-area.js';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert.js';
import { Badge } from '@/components/ui/badge.js';
import { formatPlainDate } from '@airreading/common';
import { AttendancePolicyV2Allowance, Final14DayProtection } from '@airreading/common/data';
import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';

export {
  anyStudentGroupNeedsProtectedPeriodOverride,
  protectedPeriodOverrideFromForm,
} from './protected-period-override-utils.js';

const actionLevelLabels: Record<string, string> = {
  reassignment_eligible: 'Reassignment eligible',
  warning: 'Warning',
  third_no_show: 'Reassignment eligible',
  third_late_entry: 'Reassignment eligible',
  third_early_exit: 'Reassignment eligible',
  final_14_day_protected: 'Reassignment eligible',
  final_warning: 'Warning',
  within_allowance: 'Within allowance',
};

export function Final14DayProtectedBadge({
  attendancePolicyV2,
}: {
  attendancePolicyV2?: AttendancePolicyV2Allowance | null;
}) {
  if (!attendancePolicyV2?.final14DayProtection?.isProtected) {
    return null;
  }
  const underlying =
    attendancePolicyV2.protectedActionLevel && actionLevelLabels[attendancePolicyV2.protectedActionLevel];
  return (
    <Badge
      variant="secondary"
      className="rounded-md border-amber-200 bg-amber-50 text-amber-900"
      data-testid="final-14-day-protected-badge"
    >
      Final 14 days protected
      {underlying ? ` · ${underlying}` : ''}
    </Badge>
  );
}

export function Final14DayProtectionNotice({
  attendancePolicyV2,
  final14DayProtection,
}: {
  attendancePolicyV2?: AttendancePolicyV2Allowance | null;
  final14DayProtection?: Final14DayProtection;
}) {
  const protection = final14DayProtection ?? attendancePolicyV2?.final14DayProtection;
  if (!protection?.isProtected) {
    return null;
  }
  const underlying =
    attendancePolicyV2?.protectedActionLevel && actionLevelLabels[attendancePolicyV2.protectedActionLevel];
  const finalDate = protection.finalEligiblePlainDate ? formatPlainDate(protection.finalEligiblePlainDate) : null;

  return (
    <Alert className="border-amber-200 bg-amber-50" data-testid="final-14-day-protection-notice">
      <AlertTitle>Final 14 days protected</AlertTitle>
      <AlertDescription>
        {`Attendance issues alone can't be used to reassign this teacher within 14 days of the class's final session${
          finalDate ? ` on ${finalDate}` : ''
        }.${underlying ? ` Underlying violation: ${underlying}.` : ''} Enter another reason to continue.`}
      </AlertDescription>
    </Alert>
  );
}

type OverrideFormValues = FieldValues & {
  protectedPeriodOverrideReason?: string;
};

export function ProtectedPeriodOverrideFields<T extends OverrideFormValues>({
  form,
  required,
}: {
  form: UseFormReturn<T>;
  required: boolean;
}) {
  if (!required) {
    return null;
  }

  return (
    <div className="space-y-3" data-testid="protected-period-override-fields">
      <FormTextArea
        form={form}
        name={'protectedPeriodOverrideReason' as FieldPath<T>}
        label="Reason for reassignment"
        textAreaProps={{
          required: true,
          maxLength: 500,
          placeholder: 'Explain why the teacher needs to be reassigned for a reason other than attendance issues',
          'data-testid': 'protected-period-override-reason',
        }}
      />
    </div>
  );
}
